import {computed, defineComponent, ref, watch} from 'vue';
import {
    IUpdateOperationInvoiceDataInput,
    OrganisationInvoiceGroup,
    OrganisationInvoiceGroupMember,
    OrganisationInvoiceSetting, UpdateOperationInvoiceDataInput
} from "@/api/klip-api.proxy";
import * as KlipApi from "@/api/klip-api.proxy";
import {useKlModalEventsUtil} from "@/app/shared/helpers/kl-modal-events-util";
import {useUserStore} from "@/app/shared/state/UserDataModule";
import {useKlipApiProxy} from "@/plugins/proxy-client";
import {EUserRole} from "@/app/shared/state/user-role";

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlOrganisationInvoiceGroups',
    props: {
        organisation: {
            type: Object as () => KlipApi.OrganisationDetail,
            required: true,
        },
        invoiceSettings: {
            type: Object as () => KlipApi.InvoiceSettingsForOrganisation,
            required: true
        },
        invoiceData: {
            type: Object as () => KlipApi.OrganisationInvoiceData,
            required: true,
        }
    },
    setup(props) {
        const deleteInvoiceGroupModalEvents = useKlModalEventsUtil('delete-invoice-group', {
            onClosed() {
                organisationInvoiceGroupToDelete.value = null;
                deletingOrganisationInvoiceGroup.value = false;
            },
        });

        const subOrganisations = ref<KlipApi.SubOrganisation[]>([]);
        const deletingOrganisationInvoiceGroup = ref<boolean>(false);
        const subOrganisationsInvoiceSetting = ref<OrganisationInvoiceSetting[]>([]);
        const organisationInvoiceData = ref<OrganisationInvoiceSetting>(null);
        const organisationInvoiceSettingToUpdate = ref<OrganisationInvoiceSetting>(new OrganisationInvoiceSetting({}));
        const organisationInvoiceGroups = ref<OrganisationInvoiceGroup[]>([]);
        const organisationInvoiceGroupToDelete = ref<OrganisationInvoiceGroup>(null);
        const organisationInvoiceGroupToUpdate = ref<OrganisationInvoiceGroup>(new OrganisationInvoiceGroup({}));
        const organisationsAlreadyInGroup = ref<OrganisationInvoiceGroupMember[]>([]);
        const noSubOrganisationInvoiceSettingAndNoGroups = ref<boolean>(false);

        const hasSubOrganisations = computed(() => {
            return props.organisation.organisationsDataTable.filter(o => o.lockedOut === false).length > 1;
        });

        const isAdmin = computed((): boolean => {
            return useUserStore().hasRole([EUserRole.admin]);
        });

        const title = computed((): string => {
            return hasSubOrganisations.value ? 'Facturatiegroepen' : 'Facturatiegegevens';
        });

        const updateSubOrganisationSettings = async (organisationInvoiceSettingToSave: OrganisationInvoiceSetting) => {
            const index = subOrganisationsInvoiceSetting.value.findIndex(o => o.klipOrganisationId === organisationInvoiceSettingToSave.klipOrganisationId);
            if (index >= 0) {
                subOrganisationsInvoiceSetting.value[index].attention = organisationInvoiceSettingToSave.attention;
                subOrganisationsInvoiceSetting.value[index].reference = organisationInvoiceSettingToSave.reference;
                subOrganisationsInvoiceSetting.value[index].email = organisationInvoiceSettingToSave.email;
                subOrganisationsInvoiceSetting.value[index].invoiceContactName = organisationInvoiceSettingToSave.invoiceContactName;
                subOrganisationsInvoiceSetting.value[index].invoiceContactPhoneNumber = organisationInvoiceSettingToSave.invoiceContactPhoneNumber;
                subOrganisationsInvoiceSetting.value[index].invoiceContactEmail = organisationInvoiceSettingToSave.invoiceContactEmail;
            }else if(!hasSubOrganisations.value) {
                organisationInvoiceData.value.attention = organisationInvoiceSettingToSave.attention;
                organisationInvoiceData.value.reference = organisationInvoiceSettingToSave.reference;
                organisationInvoiceData.value.email = organisationInvoiceSettingToSave.email;
                organisationInvoiceData.value.invoiceContactName = organisationInvoiceSettingToSave.invoiceContactName;
                organisationInvoiceData.value.invoiceContactPhoneNumber = organisationInvoiceSettingToSave.invoiceContactPhoneNumber;
                organisationInvoiceData.value.invoiceContactEmail = organisationInvoiceSettingToSave.invoiceContactEmail;
            }
        }

        const loadOrganisationInvoiceSettings = async () => {
            const response = await useKlipApiProxy().operations_GetOrganisationInvoiceSettings(props.organisation.organisationId);
            subOrganisationsInvoiceSetting.value = response.result.subOrganisationInvoiceSettings
            organisationInvoiceGroups.value =  response.result.organisationInvoiceGroups;
            organisationsAlreadyInGroup.value =  response.result.organisationInvoiceGroups?.map(o => o.organisations).flat();
            noSubOrganisationInvoiceSettingAndNoGroups.value = response.result.subOrganisationInvoiceSettings.length === 0 && response.result.organisationInvoiceGroups.length === 0;
        }

        const saveOrganisationInvoiceGroup = async (organisationInvoiceGroup: OrganisationInvoiceGroup) => {
            if (!organisationInvoiceGroup.id) {
                organisationInvoiceGroup.parentOrganisationId = props.organisation.organisationId;
                await useKlipApiProxy().settings_CreateOrganisationInvoiceGroup(organisationInvoiceGroup);
            } else {
                await useKlipApiProxy().settings_UpdateOrganisationInvoiceGroup(organisationInvoiceGroup);
            }
            await loadOrganisationInvoiceSettings();
        };

        const cancelOrganisationInvoiceGroup = () => {
            organisationsAlreadyInGroup.value = organisationInvoiceGroups.value.map(o => o.organisations).flat();
        }

        const updateOrganisationInvoiceGroup = (organisationInvoiceGroup: OrganisationInvoiceGroup) => {
            organisationInvoiceGroupToUpdate.value = organisationInvoiceGroup;
            organisationsAlreadyInGroup.value = organisationInvoiceGroups.value.map(o => o.organisations).flat()
                .filter(org => organisationInvoiceGroup.organisations.some(o => o.memberKlipOrganisationId !== org.memberKlipOrganisationId))
                .flat()
        };

        const showDeleteOrganisationInvoiceGroupModal = (organisationInvoiceGroup: OrganisationInvoiceGroup) => {
            organisationInvoiceGroupToDelete.value = organisationInvoiceGroup;
            deleteInvoiceGroupModalEvents.triggerModalToggle();
        };

        const deleteOrganisationInvoiceGroup = async () => {
            deletingOrganisationInvoiceGroup.value = true;
            await useKlipApiProxy().settings_DeleteOrganisationInvoiceGroup(organisationInvoiceGroupToDelete.value.id);
            await loadOrganisationInvoiceSettings();
            deleteInvoiceGroupModalEvents.triggerModalToggle();
        };

        const cancelDeleteOrganisationInvoiceGroup = () => {
            deleteInvoiceGroupModalEvents.triggerModalToggle();
        };

        const updateOrganisationInvoiceSettings = (organisationInvoiceSetting: OrganisationInvoiceSetting) => {
            organisationInvoiceSettingToUpdate.value = organisationInvoiceSetting;
        }

        const saveOrganisationInvoiceSettings = async (organisationInvoiceSettingToSave: OrganisationInvoiceSetting) => {
            const updateOperationInvoiceDataInput = {
                organisationId: organisationInvoiceSettingToSave.klipOrganisationId,
                updateChildOrganisations: false,
                email: organisationInvoiceSettingToSave.email,
                attention: organisationInvoiceSettingToSave.attention,
                reference: organisationInvoiceSettingToSave.reference,
                invoiceName: organisationInvoiceSettingToSave.invoiceContactName,
                invoiceEmail: organisationInvoiceSettingToSave.invoiceContactEmail,
                invoicePhoneNumber: organisationInvoiceSettingToSave.invoiceContactPhoneNumber
            } as UpdateOperationInvoiceDataInput;

            await useKlipApiProxy().operations_UpdateOrganisationInvoiceDetail(updateOperationInvoiceDataInput);
            await updateSubOrganisationSettings(organisationInvoiceSettingToSave);
            await loadOrganisationInvoiceSettings();
        }

        watch(() => props.invoiceSettings, (newInvoiceSettings) => {
            subOrganisationsInvoiceSetting.value = newInvoiceSettings.subOrganisationInvoiceSettings;
            organisationInvoiceGroups.value = newInvoiceSettings.organisationInvoiceGroups;
            organisationsAlreadyInGroup.value = newInvoiceSettings.organisationInvoiceGroups.map(o => o.organisations).flat();
            subOrganisations.value = props.organisation.organisationsDataTable.filter(o => !o.lockedOut);
            noSubOrganisationInvoiceSettingAndNoGroups.value = newInvoiceSettings.subOrganisationInvoiceSettings.length === 0 && newInvoiceSettings.organisationInvoiceGroups.length === 0;
        }, { immediate: true, deep: true });

        watch(() => props.invoiceData, (invoiceData) => {
            organisationInvoiceData.value = new OrganisationInvoiceSetting();
            organisationInvoiceData.value.company = invoiceData.company;
            organisationInvoiceData.value.attention = invoiceData.attention;
            organisationInvoiceData.value.reference = invoiceData.reference;
            organisationInvoiceData.value.email = invoiceData.email;
            organisationInvoiceData.value.invoiceContactName = invoiceData.invoiceName;
            organisationInvoiceData.value.invoiceContactPhoneNumber = invoiceData.invoicePhoneNumber;
            organisationInvoiceData.value.invoiceContactEmail = invoiceData.invoiceEmail;
            organisationInvoiceData.value.klipOrganisationId = props.organisation.organisationId;
        }, { immediate: true, flush: 'sync' });


        return {
            isAdmin,
            title,
            deletingOrganisationInvoiceGroup,
            hasSubOrganisations,
            organisationInvoiceData,
            organisationInvoiceGroups,
            organisationsAlreadyInGroup,
            subOrganisationsInvoiceSetting,
            noSubOrganisationInvoiceSettingAndNoGroups,
            saveOrganisationInvoiceGroup,
            cancelOrganisationInvoiceGroup,
            updateOrganisationInvoiceGroup,
            deleteOrganisationInvoiceGroup,
            cancelDeleteOrganisationInvoiceGroup,
            showDeleteOrganisationInvoiceGroupModal,
            updateOrganisationInvoiceSettings,
            saveOrganisationInvoiceSettings,
            organisationInvoiceGroupToUpdate,
            organisationInvoiceSettingToUpdate,
            subOrganisations
        }
    }
});
